import Editor, { loader } from "@monaco-editor/react";
import { ProductTemplatesApi } from "api/ProductTemplatesApi";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useEffect, useState } from "react";
import { CellType } from "utils/enums";
import { T } from "services/lang/LanguageService";
import {
  IProductTemplate,
  IProductTemplateAttribute,
  IProductTemplateConfig,
} from "../../dashboard/product-template-table/utils/interfaces";
import { easasAlert } from "../easas-global-fragment/EasasGlobalAlerts";
import ProductTemplateAttributeList from "./ProductTemplateAttributesConfiguration/ProductTemplateAttributeList";
import ProductTemplateSettingsConfiguration from "./ProductTemplateSettingsConfiguration/ProductTemplateSettingsConfiguration";
import { PRODUCT_TEMPLATE_AUTOCOMPLETE_MODEL } from "./autocomplete-model";

type TemplateTable = {
  id: bigint;
  refName: string;
  title: string;
};

type ConfiguratorProps = {
  templates: Array<TemplateTable>;
};

loader
  .init()
  .then(monaco => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions(PRODUCT_TEMPLATE_AUTOCOMPLETE_MODEL);
  })
  .catch(error =>
    console.error(T("error.an-error-occurred-during-initialization-of-monaco"), error)
  );

const defaultProductTemplate: IProductTemplate = {
  displayName: "New Template Display Name",
  metaKeyColumn: "",
  safeName: "company_some_table_name",
  keyColumn: "key_column_safe_name",
  data: {
    attributes: [
      {
        displayName: "Display name",
        type: CellType.STRING,
        referenceName: "reference_name",
      },
    ],
    parameterTemplates: [],
  },
};

const stringifyTemplate = (template: { [key: string]: any }) => JSON.stringify(template, null, 2);

const ProductTemplatesConfiguratorJson = (props: ConfiguratorProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateTable | null>(null);
  const [templateConfigJson, setTemplateConfigJson] = useState<string | undefined>(undefined);
  const [template, setTemplate] = useState(defaultProductTemplate);

  useEffect(() => {
    if (selectedTemplate) {
      reloadConfig();
    }
  }, [selectedTemplate]);

  useEffect(() => {
    const timer = setTimeout(() => setTemplateConfigJson(stringifyTemplate(template)), 500);

    return () => {
      clearTimeout(timer);
    };
  }, [template]);

  const handleClear = () => {
    setSelectedTemplate(null);
    setTemplate(defaultProductTemplate);
    setTemplateConfigJson(stringifyTemplate(defaultProductTemplate));
  };

  const onTemplateChange = (e: DropdownChangeEvent) => {
    setSelectedTemplate(e.target.value);
  };
  const onDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemplate(prev => ({ ...prev, displayName: e.target.value }));
  };
  const onRefNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTemplate(prev => ({ ...prev, safeName: e.target.value }));
  const onKeyAttrChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTemplate(prev => ({ ...prev, keyColumn: e.target.value }));
  const onMetaKeyChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTemplate(prev => ({ ...prev, metaKeyColumn: e.target.value }));

  const onAttributeChange = (attributes: IProductTemplateAttribute[]) => {
    setTemplate(prev => ({
      ...prev,
      data: prev.data ? { ...prev.data, attributes } : { attributes, parameterTemplates: [] },
    }));
  };

  const onConfigChange = (config: IProductTemplateConfig) => {
    setTemplate(prev => ({
      ...prev,
      data: prev.data
        ? { ...prev.data, config }
        : { attributes: [], parameterTemplates: [], config },
    }));
  };

  const reloadConfig = () => {
    ProductTemplatesApi.getProductTemplateConfig(selectedTemplate?.refName!).then(t => {
      if (!t.error) {
        setTemplate(t.data!);
        setTemplateConfigJson(JSON.stringify(t.data, null, 2));
      } else {
        easasAlert({ summary: t.error.message!, detail: t.error.data, severity: "error" });
      }
    });
  };

  const updateConfig = () => {
    const ref = selectedTemplate?.refName;

    const parsedTemplate = checkJsonAndMergeConfig();
    if (!parsedTemplate) return;

    ProductTemplatesApi.updateProductTemplateConfig(ref!, parsedTemplate).then(r => {
      if (!r.error) {
        easasAlert(T("alert.template-updated", { displayName: r.data?.displayName }));
      } else {
        easasAlert({ summary: r.error.message!, detail: r.error.data, severity: "error" });
      }
    });
  };

  const checkJsonAndMergeConfig = () => {
    try {
      const templateParsed = JSON.parse(templateConfigJson!);
      setTemplate(templateParsed);
      return templateParsed;
    } catch (ex: any) {
      easasAlert({ summary: `JSON ${T("alert.error")}: ${ex.message}`, severity: "error" });
      return null;
    }
  };

  const createTemplate = () => {
    const parsedTemplate = checkJsonAndMergeConfig();
    if (!parsedTemplate) return;

    ProductTemplatesApi.createProductTemplateConfig(parsedTemplate).then(r => {
      if (!r.error) {
        easasAlert(T("alert.template-name-created", { displayName: r.data?.displayName }));
      } else {
        easasAlert({ summary: r.error.message!, detail: r.error.data, severity: "error" });
      }
    });

    easasAlert(T("alert.template-created"));
  };

  const downloadProdTemplate = () => {
    const err = checkJsonAndMergeConfig();
    if (err) return;

    const fileName =
      selectedTemplate?.refName || `no_name_prod_template__${new Date().toISOString()}`;
    const json = JSON.stringify(template, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = `${fileName}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isTableSelected = selectedTemplate !== null;
  let submitButton;

  if (isTableSelected) {
    submitButton = (
      <Button
        label={T("action.save")}
        icon="pi pi-check"
        onClick={updateConfig}
        iconPos="right"
        className="p-m-2"
        style={{ width: "50%" }}
      />
    );
  } else {
    submitButton = (
      <Button
        label={T("action.create")}
        icon="pi pi-plus"
        onClick={createTemplate}
        iconPos="right"
        className="p-button-success p-m-2"
        style={{ width: "50%" }}
      />
    );
  }

  return (
    <div style={{ width: "100%", padding: "0 1rem 0 1rem" }}>
      <div className="p-fluid p-formgrid p-grid">
        {/* ===== Table selection ======= */}
        <div className="p-field p-col-11">
          <label htmlFor="type">{T("word.template")}</label>
          <Dropdown
            value={selectedTemplate}
            options={props.templates}
            filter
            showClear
            filterBy="title"
            onChange={onTemplateChange}
            optionLabel="title"
            placeholder={T("action.select-a-template")}
          />
        </div>
        {/* ===== Table selection ======= */}
        <div className="p-field p-col-1">
          <label>{T("action.download")}</label>
          <Button
            icon="pi pi-download"
            style={{ width: "100%" }}
            onClickCapture={downloadProdTemplate}
          />
        </div>
      </div>

      <TabView style={{ width: "100%" }}>
        <TabPanel header="Config">
          <div className="p-fluid p-formgrid p-grid">
            <div className="p-field p-col-12">
              <label>{T("word.display-name")}</label>
              <InputText
                placeholder={T("word.display-name")}
                value={template?.displayName ?? ""}
                onChange={onDisplayNameChange}
                className="p-inputtext-sm"
              />
            </div>
            <div className="p-field p-col-4">
              <label>Id</label>
              <InputText
                placeholder={T("word.not-initialized")}
                value={template?.id ?? ""}
                className="p-inputtext-sm"
                disabled
              />
            </div>
            <div className="p-field p-col-4">
              <label>{T("word.date-created")}</label>
              <InputText
                placeholder={T("word.date-created")}
                value={template?.dateCreated ?? ""}
                className="p-inputtext-sm"
                disabled
              />
            </div>
            <div className="p-field p-col-4">
              <label>{T("word.date-modified")}</label>
              <InputText
                placeholder={T("word.date-modified")}
                value={template?.dateModified ?? ""}
                className="p-inputtext-sm"
                disabled
              />
            </div>
            <div className="p-field p-col-12">
              <label>{T("word.reference-name")}</label>
              <InputText
                placeholder={T("word.reference-name-auto-prefixed")}
                value={template?.safeName ?? ""}
                onChange={onRefNameChange}
                className="p-inputtext-sm"
                readOnly={isTableSelected}
              />
            </div>
            <div className="p-field p-col-6">
              <label>{T("word.key-attribute")}</label>
              <InputText
                placeholder={T("word.key-attribute")}
                value={template?.keyColumn ?? ""}
                onChange={onKeyAttrChange}
                className="p-inputtext-sm"
                disabled={false}
              />
            </div>
            <div className="p-field p-col-6">
              <label>
                {T("word.secondary-key")} <i>(meta)</i> {T("word.attribute")}
              </label>
              <InputText
                placeholder={T("word.secondary-key")}
                value={template?.metaKeyColumn ?? ""}
                onChange={onMetaKeyChange}
                className="p-inputtext-sm"
                disabled={false}
              />
            </div>

            <ProductTemplateAttributeList
              attributes={template?.data?.attributes!}
              onAttributesChange={onAttributeChange}
            />
            <ProductTemplateSettingsConfiguration
              data={template.data!}
              onSettingsChange={onConfigChange}
            />
          </div>
        </TabPanel>
        <TabPanel header="JSON">
          <Editor
            height="400px"
            defaultLanguage="json"
            value={templateConfigJson}
            onChange={setTemplateConfigJson}
            theme="vs-dark"
          />
        </TabPanel>
      </TabView>

      <div className="p-d-flex p-jc-between">
        {submitButton}
        <Button
          label={T("action.clear")}
          icon="pi pi-trash"
          onClick={handleClear}
          iconPos="right"
          className="p-button-danger p-m-2"
          style={{ width: "50%" }}
        />
      </div>
    </div>
  );
};

export default ProductTemplatesConfiguratorJson;
